import { useCallback, useRef } from "react"

import { useApiContext } from "#contexts/ApiContextProvider"
import {
  EVALUATION_STATUS,
  EvaluationConstructor,
  EvaluationTrait,
  PRINT_URL_FRESHNESS_MS,
} from "#customTypes/composedTypes/evaluationTypes"
import { invariant } from "#customTypes/types"
import { useErrorModal } from "#hooks/useErrorModal"
import {
  formatEvalForPrinting,
  getIsSkillUp,
  getTemplateId,
} from "#utils/evaluationHelpers"
import { camelToWords, ErrorType, formatError } from "#utils/textFormatters"

import type { PrintEvalProps } from "./printEvalTypes"

export type { PrintEvalItem, PrintEvalProps } from "./printEvalTypes"

const usePrintEval = () => {
  const { apiHandler } = useApiContext()
  const { generateEvalPdf, downloadFileFromUrl } = apiHandler

  const openErrorModal = useErrorModal("printing evaluation")

  // Download queue management
  const downloadQueueRef = useRef<PrintEvalProps[]>([])
  const isProcessingRef = useRef(false)

  // Note: Batch printing UI state is managed globally in ApiContextProvider

  const generateEval = useCallback(
    async ({
      studentName,
      evaluation,
      traitsById,
    }: {
      studentName: string
      evaluation: EvaluationConstructor
      traitsById: Record<string, EvaluationTrait>
    }) => {
      const { currentSkill, nextSkill, classGroup } = evaluation
      const isSkillUp = getIsSkillUp(currentSkill, nextSkill, classGroup)

      try {
        invariant(
          currentSkill != null,
          "Current skill not found on evaluation!"
        )
        invariant(nextSkill != null, "Next skill not found on evaluation!")

        const formattedEval = formatEvalForPrinting(
          studentName,
          evaluation,
          traitsById
        )

        const templateId = getTemplateId(currentSkill, isSkillUp, classGroup)

        const filename = `${camelToWords(classGroup)} - ${studentName} - ${currentSkill}${isSkillUp ? `-${nextSkill}` : ""}.pdf`

        const result = await generateEvalPdf(formattedEval, templateId)

        return { url: result.url, filename, errors: result.errors }
      } catch (error: any) {
        console.error("❌ usePrintEval generateEval error => ", error)
        // Handle validation errors specially
        if (error.isValidationError && error.validationErrors) {
          const validationContext = `
Class group: ${camelToWords(classGroup)}
Student name: ${studentName}
Current skill: ${currentSkill}
Next skill: ${nextSkill}
Skill up: ${isSkillUp}

Validation Errors:
${error.validationErrors.map((err: string, index: number) => `${index + 1}. ${err}`).join("\n")}
`
          openErrorModal(error as ErrorType, validationContext)
          return null
        }

        // Handle other errors normally
        const context = `
Class group:
${camelToWords(classGroup)}

Student name:
${studentName}

Current skill:
${currentSkill}

Next skill:
${nextSkill}

Skill up:
${isSkillUp}
`
        openErrorModal(error as ErrorType, context)
        return null
      }
    },
    [generateEvalPdf, openErrorModal]
  )

  const printEval = useCallback(
    async (printData: PrintEvalProps) => {
      const { evaluation } = printData
      const existingUrl = evaluation.printing?.url
      const ts = evaluation.printedAt ?? evaluation.lastUpdatedAt

      if (
        evaluation.status === EVALUATION_STATUS.PRINTED &&
        typeof existingUrl === "string" &&
        ts &&
        Date.now() - new Date(ts).getTime() < PRINT_URL_FRESHNESS_MS
      ) {
        const filename = evaluation.printing?.filename || "evaluation.pdf"
        try {
          await downloadFileFromUrl(existingUrl, filename)
          return
        } catch {
          // URL expired or failed — fall through to regenerate
        }
      }

      const evalResult = await generateEval(printData)
      if (!evalResult) {
        console.error("❌ evalResult is null")
        return
      }

      if (evalResult.errors?.length) {
        console.error(
          `❌ Errors in evalResult: ${evalResult.errors.join(", ")}`
        )
        return
      }

      const { url, filename } = evalResult
      await downloadFileFromUrl(url, filename)
    },
    [generateEval, downloadFileFromUrl]
  )

  const processDownloadQueue = useCallback(async () => {
    if (isProcessingRef.current || downloadQueueRef.current.length === 0) {
      return
    }

    isProcessingRef.current = true

    while (downloadQueueRef.current.length > 0) {
      const evalData = downloadQueueRef.current.shift()
      if (!evalData) break

      try {
        await printEval(evalData)
      } catch (error: any) {
        // Validation errors are already handled in generateEval, so we just log other errors
        if (!error.isValidationError) {
          const formattedError = formatError(error)
          console.error(
            `Failed to print eval for ${evalData.studentName}: ${formattedError}`
          )
        }
      }
    }

    isProcessingRef.current = false
  }, [printEval])

  const batchPrintEvals = useCallback(
    async (evalDataArray: PrintEvalProps[]) => {
      // Add all evals to the queue
      downloadQueueRef.current.push(...evalDataArray)

      // Start processing if not already running
      await processDownloadQueue()
    },
    [processDownloadQueue]
  )

  return {
    printEval,
    batchPrintEvals,
    isProcessing: isProcessingRef.current,
    itemsInQueue: downloadQueueRef.current.length,
  }
}

export default usePrintEval
